import type { ParseKeys } from 'i18next'
import type { LucideIcon } from 'lucide-react'
import { Check, Eye, EyeOff, Minus, UserCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { RoleName } from '@/components/RoleName'

import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Tag } from '@/components/ui/Tag'
import { cn } from '@/lib/cn'
import {
  ACCESS_MATRIX,
  GRANTS,
  MATRIX_ROLES,
  type AccessLevel,
} from '@/features/settings/accessMatrix'

/* Roles & permissions — the read-only reference view of PRD §3.
 *
 * There is nothing to edit here, and that is the design. The five roles are
 * fixed (AD-7) and the matrix is enforced server-side (AD-21, FR-56); what a
 * founder needs in the product is an ANSWER to "what can Pricing actually see?"
 * — the question that today gets asked on WhatsApp. Assigning a role or a grant
 * to a person is a different act and lives where the person does, on the user
 * record in Directories → Users (FR-44, EXPERIENCE.md § Directories).
 *
 * The absence of edit controls is stated rather than implied: an admin screen
 * with no buttons reads as broken or as missing permissions unless it says why
 * it is read-only. EXPERIENCE.md § Permission-Shaped UI prefers absence to
 * disabled controls, so there are no greyed-out buttons here either.
 */

/** Level → how it renders. Icon AND text, never colour alone: colour is not an
 *  accessible carrier of meaning (DESIGN.md § Colour), and this table's whole
 *  job is to be unambiguous about who sees what. */
const LEVEL_META: Record<
  AccessLevel,
  { icon: LucideIcon; labelKey: ParseKeys; tone: string }
> = {
  full: {
    icon: Check,
    labelKey: 'settings.access.levels.full',
    tone: 'text-positive',
  },
  scoped: {
    icon: UserCheck,
    labelKey: 'settings.access.levels.scoped',
    tone: 'text-brand',
  },
  read: {
    icon: Eye,
    labelKey: 'settings.access.levels.read',
    tone: 'text-text-body',
  },
  /* Distinct from `none` on purpose: the field is absent from the DTO, not
     merely refused (AD-6). The UI says "hidden" so a Sales user reading a case
     understands the blank is a rule, not missing data. */
  hidden: {
    icon: EyeOff,
    labelKey: 'settings.access.levels.hidden',
    tone: 'text-attention',
  },
  none: {
    icon: Minus,
    labelKey: 'settings.access.levels.none',
    tone: 'text-text-muted',
  },
}

function LevelCell({
  level,
  detailKey,
}: {
  level: AccessLevel
  detailKey?: ParseKeys
}) {
  const { t } = useTranslation()
  const meta = LEVEL_META[level]
  const Icon = meta.icon

  return (
    <div className="flex flex-col gap-0.5">
      <span className={cn('flex items-center gap-1.5 text-[12.5px]', meta.tone)}>
        <Icon size={13} strokeWidth={2} aria-hidden />
        {t(meta.labelKey)}
      </span>
      {/* The qualifier the level alone loses — "own only", "assigned". Without
          it `scoped` in three columns looks like one rule when it is three. */}
      {detailKey ? (
        <span className="text-text-muted text-[11.5px] leading-snug font-light">
          {t(detailKey)}
        </span>
      ) : null}
    </div>
  )
}

export function AccessMatrixPanel() {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-3.5">
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.access.title')}</CardTitle>
          <p className="text-text-muted text-[12.5px] leading-snug font-light">
            {t('settings.access.subtitle')}
          </p>
        </CardHeader>

        {/* Wide by nature — six columns of prose. It scrolls inside its own
            container so the page never scrolls sideways. */}
        <div className="asa-scroll overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <caption className="sr-only">{t('settings.access.tableCaption')}</caption>
            <thead>
              <tr className="border-line border-b">
                <th scope="col" className="asa-eyebrow h-9 min-w-[220px] pr-3 pl-[18px]">
                  {t('settings.access.dataGroup')}
                </th>
                {MATRIX_ROLES.map((role) => (
                  <th
                    key={role}
                    scope="col"
                    className="asa-eyebrow h-9 min-w-[130px] px-3"
                  >
                    <RoleName role={role} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ACCESS_MATRIX.map((row) => (
                <tr
                  key={row.id}
                  className="border-line-hair border-b align-top last:border-b-0"
                >
                  <th
                    scope="row"
                    className="py-2.5 pr-3 pl-[18px] text-left font-normal"
                  >
                    <span className="text-text-heading block text-[13px]">
                      {t(row.labelKey)}
                    </span>
                    {/* The requirement each row is transcribed from. This table
                        is documentation, not enforcement — citing the source is
                        what lets it be checked against the server by hand. */}
                    <span className="text-text-muted mt-0.5 block font-mono text-[11px]">
                      {row.source}
                    </span>
                  </th>
                  {MATRIX_ROLES.map((role) => (
                    <td key={role} className="px-3 py-2.5">
                      <LevelCell
                        level={row.levels[role]}
                        detailKey={row.detailKey?.[role]}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border-line-hair text-text-muted border-t px-[18px] py-2.5 text-[12px] leading-snug font-light">
          {t('settings.access.enforcementNote')}
        </div>
      </Card>

      <div className="grid gap-3.5 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('settings.access.grantsTitle')}</CardTitle>
            <p className="text-text-muted text-[12.5px] leading-snug font-light">
              {t('settings.access.grantsSubtitle')}
            </p>
          </CardHeader>
          <CardBody className="flex flex-col pt-2">
            {GRANTS.map((grant) => (
              <div
                key={grant.id}
                className="border-line-hair flex flex-col gap-0.5 border-b py-2.5 last:border-b-0"
              >
                <span className="text-text-heading text-[13px]">
                  {t(grant.labelKey)}
                </span>
                <span className="text-text-body text-[12px] leading-snug font-light">
                  {t(grant.descriptionKey)}
                </span>
              </div>
            ))}
          </CardBody>
          <div className="border-line-hair text-text-muted border-t px-[18px] py-2.5 text-[12px] leading-snug font-light">
            {t('settings.access.grantsNote')}
          </div>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('settings.access.boundariesTitle')}</CardTitle>
          </CardHeader>
          <CardBody className="flex flex-col gap-3 pt-2">
            {/* The two boundary notes from PRD §3. They are the questions the
                matrix reliably raises — "why does Sales see money at all?" and
                "are files redacted too?" — so they are answered beside it. */}
            {(['ownDealMoney', 'agentIdentityScope'] as const).map((note) => (
              <div key={note} className="flex flex-col gap-1">
                <span className="asa-eyebrow">
                  {t(`settings.access.boundaries.${note}.title`)}
                </span>
                <p className="text-text-body text-[12.5px] leading-snug font-light">
                  {t(`settings.access.boundaries.${note}.body`)}
                </p>
              </div>
            ))}

            <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
              <Tag tone="brand">{t('settings.access.externalTag')}</Tag>
              <span className="text-text-muted text-[12px] leading-snug font-light">
                {t('settings.access.externalNote')}
              </span>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}
